import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AIIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
  TextFields as TextFieldsIcon,
  PersonAdd as PersonAddIcon,
  Translate as TranslateIcon,
  VerifiedUser as VerifiedUserIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { dokumenteApi, adressenApi, llmApi, textbausteineApi, benutzerApi } from '../services/api';
import { dokumenteService, TranslationResult } from '../services/dokumenteService';
import { zertifikateService, ZertifikatInfo, SignatureResult } from '../services/zertifikateService';
import type { GeneriertesDokument, Adresse, Textbaustein } from '../types/api';
import OnlyOfficeEditor, { OnlyOfficeEditorHandle } from '../components/OnlyOfficeEditor';
import SyncfusionEditor, { SyncfusionEditorHandle } from '../components/SyncfusionEditor';
import SyncfusionViewer from '../components/SyncfusionViewer';
import SyncfusionPdfViewer, { SyncfusionPdfViewerHandle } from '../components/SyncfusionPdfViewer';
import SyncfusionSpreadsheet from '../components/SyncfusionSpreadsheet';
import DrawIcon from '@mui/icons-material/Draw';
import ArchivDialog from '../components/ArchivDialog';
import PortalShareDialog from '../components/PortalShareDialog';
import VoiceInputButton from '../components/VoiceInputButton';
import { marked } from 'marked';
import { getApiBaseUrl } from '../utils/apiUrl';

// Hilfsfunktion: Konvertiert Newlines in HTML-Zeilenumbrüche für TipTap
const convertNewlinesToHtml = (text: string): string => {
  if (!text) return '';

  const hasNewlines = text.includes('\n');
  const hasParagraphTags = text.includes('<p>') && text.includes('</p>');

  if (hasParagraphTags && !hasNewlines) {
    return text;
  }

  if (hasNewlines) {
    const lines = text.split('\n');
    let html = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) {
        if (i < lines.length - 1) {
          html += '<p></p>';
        }
      } else if (line.startsWith('<') && (line.includes('</') || line.endsWith('>'))) {
        html += line;
      } else {
        html += '<p>' + line + '</p>';
      }
    }

    return html;
  }

  return '<p>' + text + '</p>';
};

const DokumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dokumentId = parseInt(id || '0');

  const [dokument, setDokument] = useState<GeneriertesDokument | null>(null);
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [textbausteine, setTextbausteine] = useState<Textbaustein[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editor ref for inserting content at cursor position (supports all editors)
  const editorRef = useRef<OnlyOfficeEditorHandle | SyncfusionEditorHandle>(null);

  // PDF Viewer ref for PDF documents
  const pdfViewerRef = useRef<SyncfusionPdfViewerHandle>(null);
  const [pdfSaving, setPdfSaving] = useState(false);

  // Signature placement state
  const [signaturePlacementMode, setSignaturePlacementMode] = useState(false);
  const [pendingSignatureData, setPendingSignatureData] = useState<string | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Pending signature position (wird erst beim Speichern permanent eingefügt)
  const [pendingSignaturePosition, setPendingSignaturePosition] = useState<{
    pageNumber: number;
    x: number;
    y: number;
    signatureData: string;
    // Für die visuelle Vorschau
    screenX: number;
    screenY: number;
  } | null>(null);

  // Drag-and-Drop State für Unterschrift-Positionierung
  const [isDraggingSignature, setIsDraggingSignature] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Konstanten für Unterschrift-Größe (in Bildschirm-Pixel für Vorschau)
  const SIGNATURE_PREVIEW_WIDTH = 150; // Pixel
  const SIGNATURE_PREVIEW_HEIGHT = 60; // Pixel
  // PDF-Punkte: 72 DPI, Bildschirm: 96 DPI -> Umrechnung: pixels * 72/96 = points
  const SIGNATURE_PDF_WIDTH = Math.round(SIGNATURE_PREVIEW_WIDTH * 72 / 96); // ~112 Punkte
  const SIGNATURE_PDF_HEIGHT = Math.round(SIGNATURE_PREVIEW_HEIGHT * 72 / 96); // ~45 Punkte

  // Edit fields
  const [betreff, setBetreff] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [adresseID, setAdresseID] = useState<number | undefined>(undefined);
  const [fusszeile, setFusszeile] = useState('');

  // AI Enhancement State
  const [, setAiEnhancing] = useState(false);

  // Preview
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  // AI
  const [llmGenerating, setLlmGenerating] = useState(false);

  // Archive
  const [archivDialogOpen, setArchivDialogOpen] = useState(false);

  // Portal Share
  const [portalShareDialogOpen, setPortalShareDialogOpen] = useState(false);

  // Translation
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translationResult, setTranslationResult] = useState<TranslationResult | null>(null);
  const [targetLanguage, setTargetLanguage] = useState('de');
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [translationProvider, setTranslationProvider] = useState('auto');

  // Supported languages for translation
  const supportedLanguages: Record<string, string> = {
    auto: 'Automatisch erkennen',
    de: 'Deutsch',
    en: 'Englisch',
    fr: 'Französisch',
    it: 'Italienisch',
    es: 'Spanisch',
    pt: 'Portugiesisch',
    nl: 'Niederländisch',
    pl: 'Polnisch',
    ru: 'Russisch',
  };

  // Digital Signature State
  const [digitalSignDialogOpen, setDigitalSignDialogOpen] = useState(false);
  const [digitalSigning, setDigitalSigning] = useState(false);
  const [zertifikate, setZertifikate] = useState<ZertifikatInfo[]>([]);
  const [selectedZertifikatId, setSelectedZertifikatId] = useState<number | undefined>(undefined);
  const [zertifikatPasswort, setZertifikatPasswort] = useState('');
  const [signaturGrund, setSignaturGrund] = useState('Digitale Freigabe');
  const [signaturOrt, setSignaturOrt] = useState('');
  const [signatureResult, setSignatureResult] = useState<SignatureResult | null>(null);

  // PDF-Erkennung basierend auf Dateipfad
  const isPdfDocument = dokument?.dateiPfad?.toLowerCase().endsWith('.pdf') || false;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dokumentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dokumentResponse, adressenResponse, textbausteineResponse] = await Promise.all([
        dokumenteApi.getById(dokumentId),
        adressenApi.getAll({ take: 500 }),
        textbausteineApi.getAll(),
      ]);

      const dok = dokumentResponse.data;
      console.log('=== DOKUMENT GELADEN ===');
      console.log('DokumentID:', dok.dokumentID);
      console.log('DateiPfad:', dok.dateiPfad);
      console.log('DokumentTyp:', dok.dokumentTyp);
      console.log('Betreff:', dok.betreff);
      console.log('========================');
      setDokument(dok);
      setBetreff(dok.betreff);
      setInhalt(convertNewlinesToHtml(dok.inhaltHtml || ''));
      setAdresseID(dok.adresseID);
      setFusszeile(dok.fusszeile || '');
      setAdressen(adressenResponse.data);
      setTextbausteine(textbausteineResponse.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden des Dokuments');
    } finally {
      setLoading(false);
    }
  };

  const _convertMarkdownToHtml = (markdown: string): string => {
    try {
      const lines = markdown.split('\n');
      const processedLines = lines.map(line => {
        if (line.trim() === '') {
          return '<br>';
        }
        return marked.parseInline(line) as string;
      });
      return processedLines.join('<br>');
    } catch (error) {
      console.error('Fehler bei Markdown-Konvertierung:', error);
      return markdown;
    }
  };
  void _convertMarkdownToHtml; // Suppress unused warning

  const handleSave = async () => {
    if (!dokument) return;

    try {
      setSaving(true);

      // Für Syncfusion-Editor: Dokument als Blob speichern
      if (dokument.editorTyp === 'Syncfusion' && editorRef.current) {
        const syncfusionEditor = editorRef.current as SyncfusionEditorHandle;
        if (syncfusionEditor.getDocumentAsBlob) {
          const blob = await syncfusionEditor.getDocumentAsBlob();
          if (blob) {
            // Blob als FormData an Server senden
            const formData = new FormData();
            formData.append('file', blob, 'document.docx');

            const apiUrl = getApiBaseUrl();
            const response = await fetch(`${apiUrl}/api/syncfusion/SaveDocument/${dokument.dokumentID}`, {
              method: 'POST',
              body: formData,
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || 'Fehler beim Speichern des Dokuments');
            }

            console.log('Syncfusion-Dokument erfolgreich gespeichert');
          }
        }
      }

      // Datenbankfelder aktualisieren (für alle Editor-Typen)
      // Bei Syncfusion: skipFileRegeneration=true, da die Datei bereits via Blob gespeichert wurde
      await dokumenteApi.updateContent(dokument.dokumentID, {
        betreff: betreff,
        inhalt: dokument.editorTyp === 'Syncfusion' ? '' : inhalt,
        adresseID: adresseID,
        fusszeile: fusszeile,
        skipFileRegeneration: dokument.editorTyp === 'Syncfusion',
      });

      // Update local state
      setDokument({
        ...dokument,
        betreff: betreff,
        adresseID: adresseID,
        fusszeile: fusszeile,
      });

      alert('Dokument erfolgreich gespeichert!');
    } catch (err: any) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAIImprove = async () => {
    // Für Syncfusion/OnlyOffice Editor: Text aus dem Editor holen
    if (!editorRef.current) {
      alert('Editor nicht verfügbar');
      return;
    }

    try {
      // Versuche zuerst ausgewählten Text zu bekommen
      const syncfusionEditor = editorRef.current as SyncfusionEditorHandle;
      let textToImprove = '';
      let isSelection = false;

      if (syncfusionEditor.getSelectedText) {
        textToImprove = syncfusionEditor.getSelectedText();
        if (textToImprove && textToImprove.trim()) {
          isSelection = true;
        }
      }

      // Wenn keine Auswahl, frage ob ganzes Dokument verbessert werden soll
      if (!textToImprove || !textToImprove.trim()) {
        if (syncfusionEditor.getDocumentText) {
          textToImprove = syncfusionEditor.getDocumentText();
        }
        if (!textToImprove || !textToImprove.trim()) {
          alert('Bitte markieren Sie zuerst Text im Dokument, den Sie verbessern möchten.');
          return;
        }

        const confirmAll = window.confirm(
          'Es ist kein Text ausgewählt. Möchten Sie den gesamten Dokumenttext mit KI verbessern?\n\n' +
          'Klicken Sie auf "Abbrechen" um stattdessen nur bestimmten Text auszuwählen und zu verbessern.'
        );
        if (!confirmAll) {
          return;
        }
        isSelection = false;
      }

      setLlmGenerating(true);

      // Finde die ausgewählte Adresse für die Anrede
      const selectedAdresse = adresseID ? adressen.find(a => a.adresseID === adresseID) : undefined;

      const response = await llmApi.improveText({
        originalText: textToImprove,
        verbesserungsziel: isSelection
          ? 'Verbessere den folgenden ausgewählten Text: Mache ihn professioneller, klarer und grammatikalisch korrekt. Behalte den Inhalt bei.'
          : 'Verbessere den gesamten Dokumenttext: Mache ihn professioneller, klarer und grammatikalisch korrekt. Behalte den Inhalt und die Struktur bei.',
        // Empfängerdaten aus der ausgewählten Adresse
        empfaengerAnrede: selectedAdresse?.anrede,
        empfaengerTitel: selectedAdresse?.titel,
        empfaengerVorname: selectedAdresse?.vorname,
        empfaengerNachname: selectedAdresse?.nachname,
        empfaengerFirma: selectedAdresse?.firma,
      });

      if (response.data.text) {
        // Füge den verbesserten Text ein (ersetzt die Auswahl oder den gesamten Text)
        if (isSelection) {
          syncfusionEditor.replaceSelection(response.data.text);
        } else {
          // Bei ganzem Dokument: Alles auswählen und ersetzen
          // Selectiere alles und ersetze
          const editor = editorRef.current as any;
          if (editor.containerRef?.current?.documentEditor?.selection) {
            editor.containerRef.current.documentEditor.selection.selectAll();
            syncfusionEditor.replaceSelection(response.data.text);
          } else {
            // Fallback: Text einfügen
            syncfusionEditor.insertText(response.data.text);
          }
        }
      }
    } catch (err: any) {
      alert('Fehler bei der KI-Verbesserung: ' + (err.response?.data?.message || err.message));
    } finally {
      setLlmGenerating(false);
    }
  };

  const handleOpenPreview = () => {
    setPreviewDialogOpen(true);
  };

  const handleDownload = async (format?: string) => {
    if (!dokument) return;

    try {
      const response = await dokumenteApi.download(dokument.dokumentID, format);
      const contentDisposition = response.headers['content-disposition'];
      const extension = format ? `.${format}` : '.pdf';
      let fileName = `dokument_${dokument.dokumentID}${extension}`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=(['"]?)(.+?)\1/);
        if (fileNameMatch && fileNameMatch.length > 2) {
          fileName = fileNameMatch[2];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      alert('Fehler beim Herunterladen');
    }
  };

  const handleInsertTextbaustein = async (textbaustein: Textbaustein | null) => {
    if (!textbaustein) return;

    try {
      let content = '';

      // Check if AI enhancement is enabled for this textbaustein
      if (textbaustein.kiVerbessern) {
        setAiEnhancing(true);
        try {
          const response = await textbausteineApi.enhanceWithAI(textbaustein.bausteinID);
          content = response.data.content || '';
        } catch (aiErr: any) {
          setAiEnhancing(false);
          alert('Fehler bei der KI-Verbesserung: ' + (aiErr.response?.data?.message || aiErr.message));
          return;
        }
        setAiEnhancing(false);
      } else {
        // Fetch the full content of the textbaustein without AI enhancement
        const response = await textbausteineApi.getContent(textbaustein.bausteinID);
        content = response.data.content || '';
      }

      // Convert textbaustein content to HTML if it contains newlines
      const htmlContent = convertNewlinesToHtml(content);

      // Insert the textbaustein content at the cursor position using the editor ref
      if (editorRef.current) {
        editorRef.current.insertContent(htmlContent);
      } else {
        // Fallback: append to the end if ref is not available
        setInhalt((prevInhalt) => {
          if (!prevInhalt || prevInhalt === '<p></p>') {
            return htmlContent;
          }
          return prevInhalt + '<p></p>' + htmlContent;
        });
      }
    } catch (err: any) {
      setAiEnhancing(false);
      alert('Fehler beim Laden des Textbausteins: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleInsertUnterschrift = async () => {
    try {
      console.log('DEBUG: Starting signature insertion...');
      // Fetch signature from backend
      const response = await benutzerApi.getUnterschrift();
      const blob = response.data;
      console.log('DEBUG: Blob received:', blob, 'Type:', blob.type, 'Size:', blob.size);

      // Convert blob to base64 data URL so it persists in the document
      const base64data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log('DEBUG: Base64 conversion complete, length:', (reader.result as string).length);
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      console.log('DEBUG: Base64 data starts with:', base64data.substring(0, 100));
      // Insert the signature image into the editor as base64
      const imgHtml = `<img src="${base64data}" alt="Unterschrift" style="max-width: 300px; max-height: 100px;" />`;

      if (editorRef.current) {
        console.log('DEBUG: Inserting into editor');
        editorRef.current.insertContent(imgHtml);
        // Wait a moment for the editor to update
        setTimeout(() => {
          const currentContent = editorRef.current?.getHTML();
          console.log('DEBUG: Editor content after insertion:', currentContent);
        }, 100);
      } else {
        console.log('DEBUG: Fallback - setting content directly');
        // Fallback: append to the end if ref is not available
        setInhalt((prevInhalt) => {
          if (!prevInhalt || prevInhalt === '<p></p>') {
            return imgHtml;
          }
          return prevInhalt + '<p></p>' + imgHtml;
        });
      }
      console.log('DEBUG: Signature insertion complete');
    } catch (err: any) {
      console.error('DEBUG: Error occurred:', err);
      if (err.response?.status === 404) {
        alert('Sie haben noch keine Unterschrift hinterlegt. Bitte gehen Sie zu den Einstellungen, um eine Unterschrift zu hinterlegen.');
      } else {
        console.error('Fehler beim Laden der Unterschrift:', err);
        alert('Fehler beim Laden der Unterschrift: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Funktion zum Starten des Unterschrift-Platzierungsmodus
  const handleInsertUnterschriftPdf = async () => {
    try {
      console.log('DEBUG: Fetching signature for placement mode...');

      // Fetch signature from backend
      const response = await benutzerApi.getUnterschrift();
      const blob = response.data;
      console.log('DEBUG: Blob received:', blob, 'Type:', blob.type, 'Size:', blob.size);

      // Convert blob to base64 data URL
      const base64data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log('DEBUG: Base64 conversion complete, length:', (reader.result as string).length);
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Store signature and enable placement mode
      setPendingSignatureData(base64data);
      setSignaturePlacementMode(true);
      console.log('DEBUG: Signature placement mode enabled. Click on PDF to place signature.');
    } catch (err: any) {
      console.error('DEBUG: Error occurred:', err);
      if (err.response?.status === 404) {
        alert('Sie haben noch keine Unterschrift hinterlegt. Bitte gehen Sie zu "Meine Einstellungen", um eine Unterschrift zu hinterlegen.');
      } else {
        console.error('Fehler beim Laden der Unterschrift:', err);
        alert('Fehler beim Laden der Unterschrift: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Funktion zum Abbrechen des Platzierungsmodus
  const cancelSignaturePlacement = () => {
    setSignaturePlacementMode(false);
    setPendingSignatureData(null);
  };

  // Funktion zum Verarbeiten des Klicks auf das PDF für die Unterschriftsplatzierung
  const handlePdfClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (!signaturePlacementMode || !pendingSignatureData) return;

    try {
      const container = pdfContainerRef.current;
      if (!container) return;

      // Finde das tatsächliche PDF-Seiten-Element (canvas oder div mit der gerenderten Seite)
      // Syncfusion verwendet .e-pv-page-div für die Seiten
      const pageElements = container.querySelectorAll('[id^="pdfViewer_"][id*="_pageDiv_"]');

      let pageNumber = 1;
      let pageX = 0;
      let pageY = 0;
      let foundPage = false;

      // Durchsuche alle Seiten und finde die angeklickte
      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;
        const pageRect = pageElement.getBoundingClientRect();

        if (
          event.clientX >= pageRect.left &&
          event.clientX <= pageRect.right &&
          event.clientY >= pageRect.top &&
          event.clientY <= pageRect.bottom
        ) {
          // Extrahiere die Seitennummer aus der ID (Format: pdfViewer_XXX_pageDiv_N)
          const idMatch = pageElement.id.match(/_pageDiv_(\d+)/);
          pageNumber = idMatch ? parseInt(idMatch[1]) + 1 : i + 1;

          // Klick-Position relativ zur Seite
          const clickXOnPage = event.clientX - pageRect.left;
          const clickYOnPage = event.clientY - pageRect.top;

          // Ermittle den Zoom-Level aus dem Zoom-Dropdown oder berechne ihn
          const zoomInput = container.querySelector('.e-pv-zoom-in-button')?.closest('.e-toolbar-item')
            ?.parentElement?.querySelector('input') as HTMLInputElement;
          let zoomPercent = 100;
          if (zoomInput && zoomInput.value) {
            zoomPercent = parseFloat(zoomInput.value.replace('%', '')) || 100;
          } else {
            // Fallback: Berechne Zoom aus tatsächlicher vs. Standard-Seitengröße
            // Standard A4 bei 96 DPI: 595/72*96 = 793.33 Pixel bei 100%
            const standardWidthAt100 = 595 / 72 * 96;
            zoomPercent = (pageRect.width / standardWidthAt100) * 100;
          }

          const zoomFactor = zoomPercent / 100;

          // Standard A4 PDF ist 595 x 842 Punkte
          const _pdfPageWidth = 595;
          const _pdfPageHeight = 842;
          void _pdfPageWidth; void _pdfPageHeight; // Suppress unused warning

          // Konvertiere Pixel zu PDF-Punkten
          // Bei 96 DPI: 1 PDF-Punkt = 96/72 = 1.333 Pixel bei 100% Zoom
          const pixelsPerPoint = (96 / 72) * zoomFactor;

          pageX = clickXOnPage / pixelsPerPoint;
          pageY = clickYOnPage / pixelsPerPoint;

          foundPage = true;

          console.log('DEBUG: Page rect:', pageRect);
          console.log('DEBUG: Click on page:', clickXOnPage, clickYOnPage);
          console.log('DEBUG: Zoom:', zoomPercent + '%', 'Factor:', zoomFactor);
          console.log('DEBUG: Pixels per point:', pixelsPerPoint);
          console.log('DEBUG: PDF coordinates:', pageX, pageY);

          break;
        }
      }

      // Fallback: Wenn keine Seite gefunden, versuche alternative Selektoren
      if (!foundPage) {
        const altPageElements = container.querySelectorAll('.e-pv-page-container');
        for (let i = 0; i < altPageElements.length; i++) {
          const pageElement = altPageElements[i] as HTMLElement;
          const pageRect = pageElement.getBoundingClientRect();

          if (
            event.clientX >= pageRect.left &&
            event.clientX <= pageRect.right &&
            event.clientY >= pageRect.top &&
            event.clientY <= pageRect.bottom
          ) {
            pageNumber = i + 1;
            const clickXOnPage = event.clientX - pageRect.left;
            const clickYOnPage = event.clientY - pageRect.top;

            // Einfache Umrechnung: Verhältnis zur Seitengröße
            const pdfPageWidth = 595;
            const pdfPageHeight = 842;

            pageX = (clickXOnPage / pageRect.width) * pdfPageWidth;
            pageY = (clickYOnPage / pageRect.height) * pdfPageHeight;

            foundPage = true;
            console.log('DEBUG: Using fallback calculation');
            break;
          }
        }
      }

      if (!foundPage) {
        console.error('Keine PDF-Seite an der Klick-Position gefunden');
        alert('Bitte klicken Sie direkt auf eine Seite des PDFs.');
        return;
      }

      console.log(`DEBUG: Final position - Page: ${pageNumber}, X: ${pageX.toFixed(2)}, Y: ${pageY.toFixed(2)} (PDF points)`);

      // Disable placement mode
      setSignaturePlacementMode(false);

      // Position speichern für visuelle Vorschau (wird erst beim Speichern permanent eingefügt)
      const containerRect = container?.getBoundingClientRect();

      // Die Screen-Position ist die obere linke Ecke der Unterschrift
      // Zentriere die Unterschrift auf die Klick-Position
      const screenX = event.clientX - (containerRect?.left || 0) - (SIGNATURE_PREVIEW_WIDTH / 2);
      const screenY = event.clientY - (containerRect?.top || 0) - (SIGNATURE_PREVIEW_HEIGHT / 2);

      setPendingSignaturePosition({
        pageNumber: pageNumber,
        x: pageX - (SIGNATURE_PDF_WIDTH / 2), // PDF-Koordinaten auch zentrieren
        y: pageY - (SIGNATURE_PDF_HEIGHT / 2),
        signatureData: pendingSignatureData,
        // Screen-Position für Vorschau (obere linke Ecke relativ zum Container)
        screenX: screenX,
        screenY: screenY,
      });

      setPendingSignatureData(null);
      console.log('DEBUG: Signature position saved. Click "PDF speichern" to permanently insert.');

    } catch (err: any) {
      console.error('DEBUG: Error placing signature:', err);
      setSignaturePlacementMode(false);
      setPendingSignatureData(null);
      alert('Fehler beim Platzieren der Unterschrift: ' + (err.response?.data?.message || err.message));
    }
  };

  // Funktion zum Speichern des PDFs mit Annotationen und ausstehender Unterschrift
  const handleSavePdf = async () => {
    if (!dokument) {
      alert('Dokument ist nicht bereit.');
      return;
    }

    try {
      setPdfSaving(true);
      console.log('DEBUG: Starting PDF save...');

      // Wenn eine ausstehende Unterschrift vorhanden ist, zuerst diese einfügen
      if (pendingSignaturePosition) {
        console.log('DEBUG: Inserting pending signature...');
        console.log('DEBUG: Position:', pendingSignaturePosition.x, pendingSignaturePosition.y);
        console.log('DEBUG: Size (PDF points):', SIGNATURE_PDF_WIDTH, SIGNATURE_PDF_HEIGHT);
        await dokumenteApi.addSignature(dokumentId, {
          signatureBase64: pendingSignaturePosition.signatureData,
          pageNumber: pendingSignaturePosition.pageNumber,
          x: pendingSignaturePosition.x,
          y: pendingSignaturePosition.y,
          width: SIGNATURE_PDF_WIDTH,
          height: SIGNATURE_PDF_HEIGHT,
        });

        // Ausstehende Unterschrift zurücksetzen
        setPendingSignaturePosition(null);
        console.log('DEBUG: Pending signature inserted successfully');
      }

      alert('PDF erfolgreich gespeichert!');
      console.log('DEBUG: PDF saved successfully');

      // Seite neu laden, um das aktualisierte PDF anzuzeigen
      window.location.reload();
    } catch (err: any) {
      console.error('Fehler beim Speichern des PDFs:', err);
      alert('Fehler beim Speichern des PDFs: ' + (err.response?.data?.message || err.message));
    } finally {
      setPdfSaving(false);
    }
  };

  // Funktion zum Entfernen der ausstehenden Unterschrift
  const handleRemovePendingSignature = () => {
    setPendingSignaturePosition(null);
  };

  // Drag-and-Drop Handler für die Unterschrift-Vorschau
  const handleSignatureDragStart = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!pendingSignaturePosition) return;

    const img = event.currentTarget;
    const rect = img.getBoundingClientRect();

    // Offset innerhalb des Bildes speichern
    setDragOffset({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });

    setIsDraggingSignature(true);
    console.log('DEBUG: Signature drag started');
  };

  const handleSignatureDragMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingSignature || !pendingSignaturePosition || !pdfContainerRef.current) return;

    event.preventDefault();

    const containerRect = pdfContainerRef.current.getBoundingClientRect();

    // Neue Screen-Position berechnen (obere linke Ecke der Unterschrift)
    // screenX/Y repräsentiert direkt die Position der oberen linken Ecke relativ zum Container
    const newScreenX = event.clientX - containerRect.left - dragOffset.x;
    const newScreenY = event.clientY - containerRect.top - dragOffset.y;

    setPendingSignaturePosition({
      ...pendingSignaturePosition,
      screenX: newScreenX,
      screenY: newScreenY,
    });
  };

  const handleSignatureDragEnd = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingSignature || !pendingSignaturePosition || !pdfContainerRef.current) {
      setIsDraggingSignature(false);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setIsDraggingSignature(false);
    console.log('DEBUG: Signature drag ended');

    // PDF-Koordinaten aus der neuen Screen-Position berechnen
    const container = pdfContainerRef.current;
    const containerRect = container.getBoundingClientRect();

    // Position der oberen linken Ecke der Unterschrift in Client-Koordinaten
    const signatureTopLeftX = containerRect.left + pendingSignaturePosition.screenX;
    const signatureTopLeftY = containerRect.top + pendingSignaturePosition.screenY;

    // Finde die PDF-Seite unter der neuen Position
    const pageElements = container.querySelectorAll('[id^="pdfViewer_"][id*="_pageDiv_"]');

    let foundPage = false;
    let newPageNumber = pendingSignaturePosition.pageNumber;
    let newX = pendingSignaturePosition.x;
    let newY = pendingSignaturePosition.y;

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i] as HTMLElement;
      const pageRect = pageElement.getBoundingClientRect();

      // Prüfe ob die Unterschrift (obere linke Ecke) auf dieser Seite ist
      if (
        signatureTopLeftX >= pageRect.left &&
        signatureTopLeftX <= pageRect.right &&
        signatureTopLeftY >= pageRect.top &&
        signatureTopLeftY <= pageRect.bottom
      ) {
        // Extrahiere die Seitennummer
        const idMatch = pageElement.id.match(/_pageDiv_(\d+)/);
        newPageNumber = idMatch ? parseInt(idMatch[1]) + 1 : i + 1;

        // Position relativ zur Seite (in Pixel)
        const posXOnPage = signatureTopLeftX - pageRect.left;
        const posYOnPage = signatureTopLeftY - pageRect.top;

        // Berechne Zoom-Faktor
        const standardWidthAt100 = 595 / 72 * 96;
        const zoomPercent = (pageRect.width / standardWidthAt100) * 100;
        const zoomFactor = zoomPercent / 100;
        const pixelsPerPoint = (96 / 72) * zoomFactor;

        // Konvertiere zu PDF-Punkten (obere linke Ecke)
        newX = posXOnPage / pixelsPerPoint;
        newY = posYOnPage / pixelsPerPoint;

        foundPage = true;
        console.log(`DEBUG: Drag end - Page: ${newPageNumber}, X: ${newX.toFixed(2)}, Y: ${newY.toFixed(2)} (PDF points)`);
        break;
      }
    }

    if (foundPage) {
      setPendingSignaturePosition({
        ...pendingSignaturePosition,
        pageNumber: newPageNumber,
        x: newX,
        y: newY,
      });
    }
  };

  // Funktion zum Einfuegen des Empfaengerblocks an der Cursor-Position
  const handleInsertAddressBlock = () => {
    if (!adresseID) {
      alert('Bitte waehlen Sie zuerst eine Adresse aus.');
      return;
    }

    const selectedAdresse = adressen.find(a => a.adresseID === adresseID);
    if (!selectedAdresse) {
      alert('Adresse nicht gefunden.');
      return;
    }

    // Baue den Adressblock als mehrzeiligen Text
    const addressLines: string[] = [];

    // Firma
    if (selectedAdresse.firma) {
      addressLines.push(selectedAdresse.firma);
    }

    // Name mit Anrede und Titel
    const nameParts: string[] = [];
    if (selectedAdresse.anrede) nameParts.push(selectedAdresse.anrede);
    if (selectedAdresse.titel) nameParts.push(selectedAdresse.titel);
    if (selectedAdresse.vorname) nameParts.push(selectedAdresse.vorname);
    if (selectedAdresse.nachname) nameParts.push(selectedAdresse.nachname);
    if (nameParts.length > 0) {
      addressLines.push(nameParts.join(' '));
    }

    // Strasse
    if (selectedAdresse.strasse) {
      addressLines.push(selectedAdresse.strasse);
    }

    // PLZ und Ort
    const plzOrt: string[] = [];
    if (selectedAdresse.plz) plzOrt.push(selectedAdresse.plz);
    if (selectedAdresse.ort) plzOrt.push(selectedAdresse.ort);
    if (plzOrt.length > 0) {
      addressLines.push(plzOrt.join(' '));
    }

    // Land (optional)
    if (selectedAdresse.land && selectedAdresse.land.toLowerCase() !== 'schweiz' && selectedAdresse.land.toLowerCase() !== 'ch') {
      addressLines.push(selectedAdresse.land);
    }

    const addressBlock = addressLines.join('\n');

    // In den Editor einfuegen
    if (editorRef.current) {
      // Fuer Syncfusion: insertText mit Zeilenumbruechen
      const syncfusionEditor = editorRef.current as SyncfusionEditorHandle;
      if (syncfusionEditor.insertText) {
        syncfusionEditor.insertText(addressBlock);
      } else {
        // Fallback: insertContent verwenden
        editorRef.current.insertContent(addressBlock);
      }
    } else {
      alert('Editor nicht verfuegbar.');
    }
  };

  // Handler für E-Mail senden aus dem SyncfusionEditor
  const handleSendEmailFromEditor = async (blob: Blob) => {
    if (!dokument) return;

    try {
      // Zuerst Dokument speichern, dann zur E-Mail-Compose-Seite navigieren
      // Das Blob wird automatisch als Anhang hinzugefügt
      const formData = new FormData();
      formData.append('file', blob, `${dokument.betreff || 'Dokument'}.docx`);

      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/syncfusion/SaveDocument/${dokument.dokumentID}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern des Dokuments');
      }

      // Zur E-Mail-Compose-Seite navigieren mit dokumentId als Query-Parameter
      navigate(`/emails/compose/new?dokumentId=${dokument.dokumentID}&betreff=${encodeURIComponent(dokument.betreff || '')}`);
    } catch (error: any) {
      console.error('Fehler beim Vorbereiten der E-Mail:', error);
      alert('Fehler beim Vorbereiten der E-Mail: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // Translation handler
  const handleTranslate = async () => {
    if (!dokument) return;

    try {
      setTranslating(true);
      setTranslationResult(null);

      const result = await dokumenteService.translateDocument(
        dokument.dokumentID,
        targetLanguage,
        sourceLanguage,
        translationProvider
      );

      setTranslationResult(result);

      if (!result.success) {
        alert('Übersetzungsfehler: ' + result.errorMessage);
      }
    } catch (err: any) {
      console.error('Fehler bei der Übersetzung:', err);
      alert('Fehler bei der Übersetzung: ' + (err.response?.data?.message || err.message));
    } finally {
      setTranslating(false);
    }
  };

  // Insert translated text into editor
  const handleInsertTranslation = () => {
    if (!translationResult?.translatedText || !editorRef.current) return;

    const syncfusionEditor = editorRef.current as SyncfusionEditorHandle;
    if (syncfusionEditor.insertText) {
      syncfusionEditor.insertText(translationResult.translatedText);
    } else {
      editorRef.current.insertContent(translationResult.translatedText);
    }

    setTranslateDialogOpen(false);
    setTranslationResult(null);
  };

  // Load certificates when digital sign dialog opens
  const handleOpenDigitalSignDialog = async () => {
    try {
      const certs = await zertifikateService.getMeineZertifikate();
      setZertifikate(certs);
      // Select default certificate if available
      const defaultCert = certs.find(c => c.istStandard && c.istGueltig);
      if (defaultCert) {
        setSelectedZertifikatId(defaultCert.zertifikatID);
      }
      setDigitalSignDialogOpen(true);
    } catch (err: any) {
      console.error('Fehler beim Laden der Zertifikate:', err);
      alert('Fehler beim Laden der Zertifikate: ' + (err.response?.data?.message || err.message));
    }
  };

  // Digital signature handler
  const handleDigitalSign = async () => {
    if (!dokument || !zertifikatPasswort) return;

    try {
      setDigitalSigning(true);
      setSignatureResult(null);

      const result = await zertifikateService.signDocument(
        dokument.dokumentID,
        zertifikatPasswort,
        selectedZertifikatId,
        signaturGrund,
        signaturOrt
      );

      setSignatureResult(result);

      if (result.success) {
        alert(`Dokument erfolgreich digital signiert!\nSigniert von: ${result.signedBy}\nDatum: ${new Date(result.signedAt || '').toLocaleString('de-DE')}`);
        setDigitalSignDialogOpen(false);
        setZertifikatPasswort('');
        // Reload to show updated status
        window.location.reload();
      } else {
        alert('Signaturfehler: ' + result.errorMessage);
      }
    } catch (err: any) {
      console.error('Fehler bei der digitalen Signatur:', err);
      alert('Fehler bei der digitalen Signatur: ' + (err.response?.data?.message || err.message));
    } finally {
      setDigitalSigning(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh" position="fixed" top={0} left={0}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dokument) {
    return (
      <Box sx={{ width: '100%', height: '100vh', p: 3, position: 'fixed', top: 0, left: 0 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Dokument nicht gefunden'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dokumente')}>
          Zurück zur Übersicht
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'fixed', top: 0, left: 0, backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 3,
          py: 2,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dokumente')}>
            Zurück
          </Button>
          <Typography variant="h5">{isPdfDocument ? 'PDF-Dokument anzeigen' : 'Dokument bearbeiten'}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          {/* Vorschau nur für nicht-PDF Dokumente */}
          {!isPdfDocument && (
            <Tooltip title="Vorschau anzeigen">
              <IconButton onClick={handleOpenPreview} color="primary">
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isPdfDocument ? "PDF herunterladen" : "Als PDF herunterladen"}>
            <IconButton onClick={() => handleDownload(isPdfDocument ? undefined : 'pdf')} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Für Portal freigeben">
            <IconButton onClick={() => setPortalShareDialogOpen(true)} color="info">
              <ShareIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="In Paperless archivieren">
            <IconButton onClick={() => setArchivDialogOpen(true)} color="secondary">
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dokument übersetzen">
            <IconButton onClick={() => setTranslateDialogOpen(true)} color="primary">
              <TranslateIcon />
            </IconButton>
          </Tooltip>
          {/* Speichern-Button nur für bearbeitbare Dokumente */}
          {!isPdfDocument && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Speichere...' : 'Speichern'}
            </Button>
          )}
        </Box>
      </Box>

      {/* KI-Zusammenfassung */}
      {dokument.zusammenfassung && (
        <Alert severity="info" icon={<AIIcon />} sx={{ mx: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            KI-Zusammenfassung:
          </Typography>
          <Typography variant="body2">{dokument.zusammenfassung}</Typography>
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', overflow: 'hidden' }}>
        {/* Left Side: Editor/Viewer */}
        <Box sx={{ flex: isPdfDocument ? '1 1 100%' : '1 1 75%', height: '100%', borderRight: isPdfDocument ? 'none' : '1px solid', borderColor: 'divider', minWidth: 0, overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{isPdfDocument ? 'PDF-Vorschau' : 'Dokumentinhalt'}</Typography>
              <Box display="flex" gap={1}>
                {isPdfDocument && (
                  <>
                    <Tooltip title="Meine gespeicherte Unterschrift in das PDF einfügen">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DrawIcon />}
                        onClick={handleInsertUnterschriftPdf}
                      >
                        Unterschrift einfügen
                      </Button>
                    </Tooltip>
                    <Tooltip title="PDF mit digitalem Zertifikat signieren (PKI)">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<VerifiedUserIcon />}
                        onClick={handleOpenDigitalSignDialog}
                      >
                        Digital signieren
                      </Button>
                    </Tooltip>
                    <Tooltip title="PDF mit Annotationen speichern">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={pdfSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSavePdf}
                        disabled={pdfSaving}
                      >
                        {pdfSaving ? 'Speichere...' : 'PDF speichern'}
                      </Button>
                    </Tooltip>
                  </>
                )}
                {!isPdfDocument && (
                  <>
                    <VoiceInputButton
                      onTranscription={(text) => {
                        // Text an der Cursor-Position im Editor einfügen
                        if (editorRef.current) {
                          const syncfusionEditor = editorRef.current as SyncfusionEditorHandle;
                          if (syncfusionEditor.insertText) {
                            syncfusionEditor.insertText(text);
                          } else {
                            editorRef.current.insertContent(text);
                          }
                        }
                      }}
                      size="small"
                    />
                    <Button
                      size="small"
                      startIcon={llmGenerating ? <CircularProgress size={16} /> : <AIIcon />}
                      onClick={handleAIImprove}
                      disabled={llmGenerating}
                      title="Markieren Sie Text im Dokument und klicken Sie hier, um ihn mit KI zu verbessern"
                    >
                      {llmGenerating ? 'Verbessere...' : 'Mit KI verbessern'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {!isPdfDocument && (
              <TextField
                fullWidth
                size="small"
                label="Betreff"
                value={betreff}
                onChange={(e) => setBetreff(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}

            <Box sx={{ mb: 2 }}>
              {/* Editor basierend auf Dateityp und EditorTyp-Feld auswählen */}
              {(() => {
                const editorType = dokument?.editorTyp || 'Syncfusion';
                const dateiPfad = dokument?.dateiPfad || '';
                const isPdf = dateiPfad.toLowerCase().endsWith('.pdf');
                const isExcel = dateiPfad.toLowerCase().endsWith('.xlsx') || dateiPfad.toLowerCase().endsWith('.xls');

                console.log('=== EDITOR-AUSWAHL ===');
                console.log('EditorTyp:', editorType);
                console.log('DateiPfad:', dateiPfad);
                console.log('IsPdf:', isPdf);
                console.log('IsExcel:', isExcel);
                console.log('======================');

                // Excel-Dokumente: Syncfusion Spreadsheet Editor anzeigen
                if (isExcel) {
                  return (
                    <SyncfusionSpreadsheet
                      documentId={dokumentId}
                      height="calc(100vh - 280px)"
                      onContentChange={() => {
                        // Optional: Änderungen tracken
                      }}
                    />
                  );
                }

                // PDF-Dokumente: PDF Viewer anzeigen
                if (isPdf) {
                  return (
                    <div
                      ref={pdfContainerRef}
                      onClick={handlePdfClick}
                      onMouseMove={handleSignatureDragMove}
                      onMouseUp={handleSignatureDragEnd}
                      onMouseLeave={handleSignatureDragEnd}
                      style={{
                        position: 'relative',
                        cursor: signaturePlacementMode ? 'crosshair' : (isDraggingSignature ? 'grabbing' : 'default'),
                      }}
                    >
                      {/* Overlay für Platzierungsmodus */}
                      {signaturePlacementMode && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            borderRadius: 1,
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '2px solid #1976d2',
                          }}
                        >
                          <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                            Klicken Sie auf die Stelle im PDF, wo die Unterschrift eingefügt werden soll
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelSignaturePlacement();
                            }}
                            sx={{ ml: 2 }}
                          >
                            Abbrechen
                          </Button>
                        </Box>
                      )}

                      {/* Hinweis für ausstehende Unterschrift */}
                      {pendingSignaturePosition && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            borderRadius: 1,
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '2px solid #4caf50',
                          }}
                        >
                          <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            Unterschrift platziert (Seite {pendingSignaturePosition.pageNumber}). Ziehen Sie die Unterschrift mit der Maus, um sie neu zu positionieren. Klicken Sie auf "PDF speichern", um die Änderung permanent zu machen.
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePendingSignature();
                            }}
                            sx={{ ml: 2 }}
                          >
                            Unterschrift entfernen
                          </Button>
                        </Box>
                      )}

                      {/* Visuelle Vorschau der Unterschrift (ziehbar) */}
                      {pendingSignaturePosition && (
                        <img
                          src={pendingSignaturePosition.signatureData}
                          alt="Unterschrift Vorschau"
                          onMouseDown={handleSignatureDragStart}
                          draggable={false}
                          style={{
                            position: 'absolute',
                            left: pendingSignaturePosition.screenX,
                            top: pendingSignaturePosition.screenY,
                            width: SIGNATURE_PREVIEW_WIDTH,
                            height: SIGNATURE_PREVIEW_HEIGHT,
                            opacity: 0.8,
                            cursor: isDraggingSignature ? 'grabbing' : 'grab',
                            zIndex: 999,
                            border: '2px dashed #4caf50',
                            borderRadius: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            userSelect: 'none',
                          }}
                        />
                      )}

                      <SyncfusionPdfViewer
                        ref={pdfViewerRef}
                        documentId={dokumentId}
                        height="calc(100vh - 280px)"
                      />
                    </div>
                  );
                }

                // Word-Dokumente: Editor basierend auf EditorTyp
                if (editorType === 'OnlyOffice') {
                  return (
                    <OnlyOfficeEditor
                      ref={editorRef as React.RefObject<OnlyOfficeEditorHandle>}
                      documentId={dokumentId}
                      height="calc(100vh - 280px)"
                    />
                  );
                } else if (editorType === 'Syncfusion') {
                  return (
                    <SyncfusionEditor
                      ref={editorRef as React.RefObject<SyncfusionEditorHandle>}
                      documentId={dokumentId}
                      height="calc(100vh - 280px)"
                      onContentChange={() => {
                        // Optional: Änderungen tracken
                      }}
                      onSendEmail={handleSendEmailFromEditor}
                    />
                  );
                } else {
                  // Syncfusion Editor als Standard (fallback)
                  return (
                    <SyncfusionEditor
                      ref={editorRef as React.RefObject<SyncfusionEditorHandle>}
                      documentId={dokumentId}
                      height="calc(100vh - 280px)"
                      onContentChange={() => {
                        // Optional: Änderungen tracken
                      }}
                      onSendEmail={handleSendEmailFromEditor}
                    />
                  );
                }
              })()}
            </Box>

            {!isPdfDocument && (
              <TextField
                fullWidth
                size="small"
                label="Fußzeile / Grußformel"
                value={fusszeile}
                onChange={(e) => setFusszeile(e.target.value)}
                multiline
                rows={2}
                placeholder="z.B. Mit freundlichen Grüßen"
              />
            )}
          </Box>
        </Box>

        {/* Right Side: Sidebar - nur für bearbeitbare Dokumente */}
        {!isPdfDocument && (
        <Box sx={{ flex: '1 1 25%', height: '100%', minWidth: 300, overflow: 'auto', backgroundColor: 'background.paper' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Einstellungen
            </Typography>

            {/* Document Info */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Dokumentvorlage
                </Typography>
                <Typography variant="body2">
                  Vorlage ID: {dokument.vorlageID}
                </Typography>
                <Typography variant="body2">
                  Typ: {dokument.dokumentTyp}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Erstellt: {new Date(dokument.erstelltAm).toLocaleDateString('de-DE')}
                </Typography>
              </CardContent>
            </Card>

            <Divider sx={{ mb: 2 }} />

            {/* Address Selection */}
            <Typography variant="subtitle2" gutterBottom>
              Empfaenger
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Adresse</InputLabel>
              <Select
                value={adresseID || ''}
                label="Adresse"
                onChange={(e) => setAdresseID(e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>Keine Adresse</em>
                </MenuItem>
                {adressen.map((adresse) => (
                  <MenuItem key={adresse.adresseID} value={adresse.adresseID}>
                    {adresse.vollstaendigerName}
                    {adresse.firma && ` (${adresse.firma})`}
                    {adresse.ort && ` - ${adresse.ort}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PersonAddIcon />}
              onClick={handleInsertAddressBlock}
              disabled={!adresseID}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Adressblock einfuegen
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Fuegt die Empfaengeradresse an der Cursor-Position ein
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Textbausteine */}
            <Typography variant="subtitle2" gutterBottom>
              Textbausteine
            </Typography>
            <Autocomplete
              options={textbausteine}
              getOptionLabel={(option) => option.bausteinName || ''}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.bausteinName}
                    </Typography>
                    {option.kategorie && (
                      <Chip size="small" label={option.kategorie} sx={{ mr: 1, mt: 0.5 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.kurzerText?.substring(0, 80)}
                      {option.kurzerText && option.kurzerText.length > 80 ? '...' : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              onChange={(_, value) => handleInsertTextbaustein(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Textbaustein suchen..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <TextFieldsIcon color="action" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
              clearOnBlur
              blurOnSelect
              value={null}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Wählen Sie einen Textbaustein aus, um ihn an der Cursor-Position einzufügen
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Unterschrift einfügen */}
            <Typography variant="subtitle2" gutterBottom>
              Unterschrift
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleInsertUnterschrift}
              sx={{ textTransform: 'none' }}
            >
              Unterschrift einfügen
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Fügt Ihre hinterlegte Unterschrift an der Cursor-Position ein
            </Typography>
          </Box>
        </Box>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Dokumentvorschau
        </DialogTitle>
        <DialogContent>
          <Box sx={{ minHeight: 500 }}>
            <SyncfusionViewer documentId={dokumentId} height="60vh" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDownload('pdf')} startIcon={<DownloadIcon />}>
            Als PDF herunterladen
          </Button>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archivierungs-Dialog */}
      <ArchivDialog
        open={archivDialogOpen}
        onClose={() => setArchivDialogOpen(false)}
        sourceType="document"
        sourceId={dokument.dokumentID}
        defaultTitle={dokument.betreff}
        onSuccess={(deleted) => {
          if (deleted) {
            navigate('/dokumente');
          }
        }}
      />

      {/* Portal-Freigabe-Dialog */}
      <PortalShareDialog
        open={portalShareDialogOpen}
        onClose={() => setPortalShareDialogOpen(false)}
        dokumentId={dokument.dokumentID}
        dokumentTitel={dokument.betreff}
        onSuccess={() => {
          // Optional: Erfolgsmeldung
        }}
      />

      {/* Übersetzungs-Dialog */}
      <Dialog
        open={translateDialogOpen}
        onClose={() => {
          setTranslateDialogOpen(false);
          setTranslationResult(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TranslateIcon color="primary" />
            Dokument übersetzen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box display="flex" gap={2} mb={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ausgangssprache</InputLabel>
                <Select
                  value={sourceLanguage}
                  label="Ausgangssprache"
                  onChange={(e) => setSourceLanguage(e.target.value)}
                >
                  {Object.entries(supportedLanguages).map(([code, name]) => (
                    <MenuItem key={code} value={code}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Zielsprache</InputLabel>
                <Select
                  value={targetLanguage}
                  label="Zielsprache"
                  onChange={(e) => setTargetLanguage(e.target.value)}
                >
                  {Object.entries(supportedLanguages)
                    .filter(([code]) => code !== 'auto')
                    .map(([code, name]) => (
                      <MenuItem key={code} value={code}>{name}</MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={translationProvider}
                  label="Provider"
                  onChange={(e) => setTranslationProvider(e.target.value)}
                >
                  <MenuItem value="auto">Automatisch</MenuItem>
                  <MenuItem value="llm">LLM (KI)</MenuItem>
                  <MenuItem value="deepl">DeepL</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={translating ? <CircularProgress size={20} color="inherit" /> : <TranslateIcon />}
              onClick={handleTranslate}
              disabled={translating}
              sx={{ mb: 3 }}
            >
              {translating ? 'Übersetze...' : 'Übersetzen'}
            </Button>

            {translationResult?.success && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Übersetzung erfolgreich ({translationResult.sourceLanguage} → {translationResult.targetLanguage})
                  {translationResult.provider && ` via ${translationResult.provider}`}
                </Alert>
                <Typography variant="subtitle2" gutterBottom>
                  Übersetzter Text:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={translationResult.translatedText || ''}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 2 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {translationResult?.success && !isPdfDocument && (
            <Button
              variant="contained"
              color="success"
              onClick={handleInsertTranslation}
              startIcon={<TextFieldsIcon />}
            >
              In Dokument einfügen
            </Button>
          )}
          {translationResult?.success && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(translationResult.translatedText || '');
                alert('Text in Zwischenablage kopiert!');
              }}
            >
              Kopieren
            </Button>
          )}
          <Button onClick={() => {
            setTranslateDialogOpen(false);
            setTranslationResult(null);
          }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Digitale Signatur Dialog */}
      <Dialog
        open={digitalSignDialogOpen}
        onClose={() => {
          setDigitalSignDialogOpen(false);
          setSignatureResult(null);
          setZertifikatPasswort('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VerifiedUserIcon color="success" />
            Dokument digital signieren
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Mit einer digitalen Signatur wird das Dokument kryptografisch signiert.
              Dies stellt die Integrität und Authentizität des Dokuments sicher.
            </Alert>

            {zertifikate.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Sie haben noch kein Zertifikat hochgeladen. Bitte laden Sie zuerst ein
                PFX/P12-Zertifikat in den Einstellungen hoch.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Zertifikat</InputLabel>
                  <Select
                    value={selectedZertifikatId || ''}
                    label="Zertifikat"
                    onChange={(e) => setSelectedZertifikatId(Number(e.target.value))}
                  >
                    {zertifikate.map((cert) => (
                      <MenuItem
                        key={cert.zertifikatID}
                        value={cert.zertifikatID}
                        disabled={!cert.istGueltig}
                      >
                        <Box>
                          <Typography variant="body1">
                            {cert.name}
                            {cert.istStandard && (
                              <Chip size="small" label="Standard" color="primary" sx={{ ml: 1 }} />
                            )}
                            {!cert.istGueltig && (
                              <Chip size="small" label="Abgelaufen" color="error" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cert.subject} • Gültig bis: {cert.gueltigBis ? new Date(cert.gueltigBis).toLocaleDateString('de-DE') : '-'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="password"
                  label="Zertifikats-Passwort"
                  value={zertifikatPasswort}
                  onChange={(e) => setZertifikatPasswort(e.target.value)}
                  sx={{ mb: 2 }}
                  required
                  helperText="Das Passwort, mit dem das PFX-Zertifikat geschützt ist"
                />

                <Divider sx={{ my: 2 }} />

                <TextField
                  fullWidth
                  label="Grund der Signatur"
                  value={signaturGrund}
                  onChange={(e) => setSignaturGrund(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="z.B. Digitale Freigabe, Genehmigt"
                />

                <TextField
                  fullWidth
                  label="Ort"
                  value={signaturOrt}
                  onChange={(e) => setSignaturOrt(e.target.value)}
                  placeholder="z.B. Zürich"
                />
              </>
            )}

            {signatureResult && !signatureResult.success && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {signatureResult.errorMessage}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDigitalSignDialogOpen(false);
            setSignatureResult(null);
            setZertifikatPasswort('');
          }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleDigitalSign}
            disabled={digitalSigning || !zertifikatPasswort || zertifikate.length === 0}
            startIcon={digitalSigning ? <CircularProgress size={20} color="inherit" /> : <VerifiedUserIcon />}
          >
            {digitalSigning ? 'Signiere...' : 'Signieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DokumentDetail;
